import { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { nip19, nip57 } from 'nostr-tools';
import { Layout } from '@/components/Layout';
import { TorchSvg } from '@/components/torch/TorchSvg';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAuthor } from '@/hooks/useAuthor';
import { useNIP05 } from '@/hooks/useNIP05';
import { useWallet } from '@/hooks/useWallet';
import { useDirectZap } from '@/hooks/useDirectZap';
import { usePublishCompanionNote } from '@/hooks/useCompanionNote';
import { useTorchBySlug } from '@/hooks/useTorchQueries';
import { useUserSearch, type UserSearchResult } from '@/hooks/useUserSearch';
import { genUserName } from '@/lib/genUserName';
import { TORCH_PASS_KIND, calculateMinimumZap } from '@/lib/torchConstants';
import { ArrowLeft, Zap, Loader2, Flame, Search, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

/** Decode an npub / nprofile / raw 64-char hex into a hex pubkey. Returns null on failure. */
function decodeRecipientPubkey(input: string): string | null {
  const trimmed = input.trim().replace(/^@/, '');
  if (/^[0-9a-f]{64}$/i.test(trimmed)) return trimmed.toLowerCase();
  try {
    const decoded = nip19.decode(trimmed);
    if (decoded.type === 'npub') return decoded.data as string;
    if (decoded.type === 'nprofile') return (decoded.data as { pubkey: string }).pubkey;
  } catch { /* fall through */ }
  return null;
}

/** Quick-add bump buttons */
const ZAP_BUMPS = [1, 21, 210, 2100];

/** Simple debounce hook */
function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

/** Single row in the search dropdown */
function UserSearchRow({ result, onSelect }: { result: UserSearchResult; onSelect: (r: UserSearchResult) => void }) {
  const { metadata, pubkey } = result;
  const displayName = metadata.name ?? metadata.display_name ?? genUserName(pubkey);
  const picture = metadata.picture;
  const nip05 = metadata.nip05;
  const initials = displayName.slice(0, 2).toUpperCase();

  let npubShort = '';
  try {
    const npub = nip19.npubEncode(pubkey);
    npubShort = npub.slice(0, 12) + '…';
  } catch { /* ignore */ }

  return (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); onSelect(result); }}
      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-orange-500/10 transition-colors text-left"
    >
      <Avatar className="w-9 h-9 shrink-0">
        <AvatarImage src={picture} alt={displayName} />
        <AvatarFallback className="text-xs font-semibold">{initials}</AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-sm truncate">{displayName}</p>
        <p className="text-xs text-muted-foreground truncate">{nip05 || npubShort}</p>
      </div>
    </button>
  );
}

/** Dropdown that appears below the recipient input while searching */
function UserSearchDropdown({
  query,
  onSelect,
}: {
  query: string;
  onSelect: (r: UserSearchResult) => void;
}) {
  const debouncedQuery = useDebounce(query, 300);
  const { data: results, isFetching } = useUserSearch(debouncedQuery);

  // Strip leading @ for display
  const displayQuery = query.startsWith('@') ? query.slice(1) : query;

  if (!query || query.length < 2) return null;

  return (
    <div className="absolute left-0 right-0 top-full mt-1 z-50 bg-popover border border-border rounded-xl shadow-xl overflow-hidden">
      {isFetching && (!results || results.length === 0) && (
        <div className="px-3 py-3 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Searching…
        </div>
      )}
      {results && results.length > 0 && (
        <div className="divide-y divide-border/40">
          {results.map((r) => (
            <UserSearchRow key={r.pubkey} result={r} onSelect={onSelect} />
          ))}
        </div>
      )}
      {results && results.length === 0 && !isFetching && (
        <div className="px-3 py-3 text-sm text-muted-foreground">
          No results for "<span className="font-medium">{displayQuery}</span>"
        </div>
      )}
    </div>
  );
}

function RecipientPreview({ pubkey }: { pubkey: string }) {
  const { data: author, isLoading } = useAuthor(pubkey);
  const metadata = author?.metadata;
  const displayName = metadata?.name ?? metadata?.display_name ?? genUserName(pubkey);
  const picture = metadata?.picture;
  const nip05 = metadata?.nip05;
  const initials = displayName.slice(0, 2).toUpperCase();

  if (isLoading) {
    return (
      <div className="flex items-center gap-3">
        <Skeleton className="w-10 h-10 rounded-full" />
        <div className="space-y-1">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-3 w-20" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3">
      <Avatar className="w-10 h-10 ring-2 ring-orange-400 ring-offset-2">
        <AvatarImage src={picture} alt={displayName} />
        <AvatarFallback className="text-sm font-semibold">{initials}</AvatarFallback>
      </Avatar>
      <div className="min-w-0">
        <p className="font-semibold text-sm leading-tight">{displayName}</p>
        <p className="text-xs text-muted-foreground truncate">
          {nip05 || `${pubkey.slice(0, 8)}…${pubkey.slice(-6)}`}
        </p>
      </div>
    </div>
  );
}

export function PassTorch() {
  const { naddr } = useParams<{ naddr: string }>();
  const navigate = useNavigate();

  const { user } = useCurrentUser();
  const { webln } = useWallet();
  const { sendZap, isZapping } = useDirectZap(webln);
  const { mutate: publishEvent, isPending: isPublishing } = useNostrPublish();
  const { publishCompanion } = usePublishCompanionNote();
  const { resolveNIP05, isResolving } = useNIP05();

  const [step, setStep] = useState<1 | 2>(1);
  const [recipientInput, setRecipientInput] = useState('');
  const [resolvedPubkey, setResolvedPubkey] = useState<string | null>(null);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [mark, setMark] = useState('');
  const [customZapInput, setCustomZapInput] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [zapError, setZapError] = useState<string | null>(null);
  const [canReceiveZaps, setCanReceiveZaps] = useState<boolean | null>(null);
  const [zapCheckError, setZapCheckError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: parsedChain, isLoading } = useTorchBySlug(naddr || '');

  // Load recipient profile once resolved so we can verify they can receive zaps
  const { data: recipientAuthor, isLoading: isRecipientLoading } = useAuthor(resolvedPubkey ?? '');

  useEffect(() => {
    if (!resolvedPubkey) {
      setCanReceiveZaps(null);
      setZapCheckError(null);
      return;
    }
    if (!recipientAuthor?.event) {
      setCanReceiveZaps(null);
      setZapCheckError(null);
      return;
    }

    // Check if the profile advertises a Lightning address at all
    const metadata = recipientAuthor.metadata;
    const hasLnAddress = !!(metadata?.lud16 || metadata?.lud06);

    let cancelled = false;
    nip57.getZapEndpoint(recipientAuthor.event)
      .then((endpoint) => {
        if (cancelled) return;
        if (endpoint) {
          setCanReceiveZaps(true);
          setZapCheckError(null);
        } else if (hasLnAddress) {
          // Profile has lud16/lud06 but getZapEndpoint couldn't resolve it
          // (CORS block, server error, or missing allowsNostr/nostrPubkey)
          setCanReceiveZaps(false);
          setZapCheckError('Could not reach this Lightning address. The recipient\'s server may block external requests or be misconfigured.');
        } else {
          setCanReceiveZaps(false);
          setZapCheckError('This user has no Lightning address on their Nostr profile. They cannot receive torches until they add one.');
        }
      })
      .catch(() => {
        if (cancelled) return;
        setCanReceiveZaps(false);
        if (hasLnAddress) {
          setZapCheckError('Could not reach this Lightning address. The recipient\'s server may block external requests or be misconfigured.');
        } else {
          setZapCheckError('This user has no Lightning address on their Nostr profile. They cannot receive torches until they add one.');
        }
      });
    return () => { cancelled = true; };
  }, [resolvedPubkey, recipientAuthor?.event]);

  const publishPass = useCallback((
    zapReceiptId: string,
    zapAmountSats: number,
    recipientPubkey: string,
  ) => {
    if (!parsedChain) return;
    // Safety guard: never publish a Pass event with an empty receipt ID —
    // it would corrupt the chain and fail validation on every future read.
    if (!zapReceiptId) return;
    const { genesis, canonicalChain } = parsedChain;
    const genesisRefStr = `30910:${genesis.pubkey}:${genesis.slug}`;

    // Previous event to chain off: last pass id, or genesis id if this is the first pass
    const previousPassId = canonicalChain.length > 0
      ? canonicalChain[canonicalChain.length - 1].pass.id
      : genesis.id;

    const expiresAt = Math.floor(Date.now() / 1000) + (genesis.ttlHours * 3600);

    publishEvent({
      kind: TORCH_PASS_KIND,
      content: '',
      tags: [
        ['a', genesisRefStr],
        ['e', previousPassId, '', 'reply'],
        ['p', recipientPubkey],
        ['zap-receipt', zapReceiptId],
        ['zap-amount', String(zapAmountSats)],
        ['mark', mark],
        ['expires_at', String(expiresAt)],
        ['alt', `Torch passed: ${genesis.title} → holder #${canonicalChain.length + 1}`],
      ],
    }, {
      onSuccess: (passEvent) => {
        publishCompanion({
          genesis,
          action: 'pass',
          recipientPubkey,
          mark,
          passEventId: passEvent.id,
          zapAmountSats,
        });
        navigate(`/torch/${genesis.slug}`);
      },
    });
  }, [parsedChain, mark, publishEvent, publishCompanion, navigate]);

  // Must be declared before any early returns to avoid rules-of-hooks violation
  const handleSelectFromDropdown = useCallback((result: UserSearchResult) => {
    setResolvedPubkey(result.pubkey);
    const displayName = result.metadata.name ?? result.metadata.display_name ?? genUserName(result.pubkey);
    setRecipientInput(displayName);
    setShowDropdown(false);
    setResolveError(null);
    setZapError(null);
    setZapCheckError(null);
    setCanReceiveZaps(null);
    setStep(2);
  }, []);

  if (isLoading) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-8">
          <Skeleton className="h-6 w-32 mb-6" />
          <div className="flex justify-center mb-8">
            <Skeleton className="w-32 h-40" />
          </div>
          <Skeleton className="h-48 w-full rounded-xl" />
        </div>
      </Layout>
    );
  }

  if (!parsedChain) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <Flame className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Torch Not Found</h1>
          <p className="text-muted-foreground mb-4">This torch does not exist or could not be loaded.</p>
          <Link to="/"><Button>← Back</Button></Link>
        </div>
      </Layout>
    );
  }

  const { genesis, canonicalChain, currentHolder, lastZapAmount } = parsedChain;
  const isCurrentHolder = user && currentHolder === user.pubkey;

  if (!user) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <Flame className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Login Required</h1>
          <p className="text-muted-foreground mb-4">You need to be logged in to pass a torch.</p>
          <Link to={`/torch/${genesis.slug}`}><Button>← Back</Button></Link>
        </div>
      </Layout>
    );
  }

  if (!isCurrentHolder) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <Flame className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Not Your Torch</h1>
          <p className="text-muted-foreground mb-4">Only the current holder can pass this torch.</p>
          <Link to={`/torch/${genesis.slug}`}><Button>← Back</Button></Link>
        </div>
      </Layout>
    );
  }

  const minimumZap = calculateMinimumZap(lastZapAmount);
  const parsedCustom = parseInt(customZapInput, 10);
  const chosenZap = !isNaN(parsedCustom) && parsedCustom >= minimumZap
    ? parsedCustom
    : minimumZap;

  const acceptChain = canonicalChain.map(link => ({
    holderPubkey: link.pass.recipientPubkey,
    zapAmountSats: link.pass.zapAmountSats,
    positionIndex: link.position,
  }));

  const handleResolveRecipient = async () => {
    setResolveError(null);
    const trimmed = recipientInput.trim().replace(/^@/, '');
    if (!trimmed) return;

    if (trimmed.includes('@')) {
      const result = await resolveNIP05(trimmed);
      if (result) {
        setResolvedPubkey(result.pubkey);
        setStep(2);
      } else {
        setResolveError('Could not resolve NIP-05 address. Check and try again.');
      }
    } else {
      const pubkey = decodeRecipientPubkey(trimmed);
      if (pubkey) {
        setResolvedPubkey(pubkey);
        setStep(2);
      } else {
        setResolveError('Invalid input. Enter a valid npub1… or user@domain.com.');
      }
    }
  };

  const handleZapAndPass = async () => {
    if (!user || !resolvedPubkey) return;
    setZapError(null);
    const result = await sendZap({
      recipientPubkey: resolvedPubkey,
      amountSats: chosenZap,
      comment: mark || `🔥 The torch "${genesis.title}" is yours!`,
    });

    if (result.success) {
      publishPass(result.zapReceiptId, chosenZap, resolvedPubkey);
    } else {
      setZapError(result.message);
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Link
          to={`/torch/${genesis.slug}`}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Link>

        {/* Torch visual */}
        <div className="mb-8 text-center">
          <TorchSvg
            genesisId={genesis.id}
            chain={acceptChain}
            state="lit"
            className="w-32 h-40 mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold">Pass the Torch</h1>
          <p className="text-muted-foreground">{genesis.title}</p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-6">
          {[1, 2].map((s) => (
            <div
              key={s}
              className={`flex-1 h-2 rounded-full transition-colors ${s <= step ? 'bg-orange-500' : 'bg-muted'}`}
            />
          ))}
        </div>

        {/* Step 1: Choose recipient */}
        {step === 1 && (
          <Card>
            <CardHeader>
              <CardTitle>Who gets the torch?</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="recipient">Search by name, npub, or NIP-05</Label>
                <div className="relative mt-2">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Input
                        ref={inputRef}
                        id="recipient"
                        placeholder="@name, npub1… or user@domain.com"
                        value={recipientInput}
                        onChange={(e) => {
                          setRecipientInput(e.target.value);
                          setResolveError(null);
                          setShowDropdown(true);
                        }}
                        onFocus={() => setShowDropdown(true)}
                        onBlur={() => setShowDropdown(false)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { setShowDropdown(false); handleResolveRecipient(); }
                          if (e.key === 'Escape') setShowDropdown(false);
                        }}
                        autoComplete="off"
                        className="w-full"
                      />
                      {showDropdown && (
                        <UserSearchDropdown
                          query={recipientInput.startsWith('@') ? recipientInput.slice(1) : recipientInput}
                          onSelect={handleSelectFromDropdown}
                        />
                      )}
                    </div>
                    <Button
                      onClick={() => { setShowDropdown(false); handleResolveRecipient(); }}
                      disabled={!recipientInput || isResolving}
                      variant="secondary"
                    >
                      {isResolving
                        ? <Loader2 className="w-4 h-4 animate-spin" />
                        : <Search className="w-4 h-4" />}
                    </Button>
                  </div>
                  {resolveError && (
                    <p className="text-xs text-destructive mt-2">{resolveError}</p>
                  )}
                </div>
              </div>

              <Button
                onClick={() => { setShowDropdown(false); handleResolveRecipient(); }}
                disabled={!recipientInput || isResolving}
                className="w-full bg-orange-500 hover:bg-orange-600"
              >
                {isResolving
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Resolving…</>
                  : <><Search className="w-4 h-4 mr-2" /> Continue</>}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Set zap amount, leave a mark, and zap */}
        {step === 2 && resolvedPubkey && (
          <Card>
            <CardHeader>
              <CardTitle>Zap to pass</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">

              {/* Recipient preview */}
              <div className="p-4 bg-muted/50 rounded-xl border border-border/50">
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-3">
                  Passing to
                </p>
                <RecipientPreview pubkey={resolvedPubkey} />
                {isRecipientLoading && (
                  <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1.5">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Checking Lightning address…
                  </p>
                )}
                {!isRecipientLoading && recipientAuthor?.metadata && (
                  <div className="mt-2 space-y-1">
                    {(recipientAuthor.metadata.lud16 || recipientAuthor.metadata.lud06) ? (
                      <p className="text-xs text-orange-600 font-medium flex items-center gap-1">
                        <Zap className="w-3 h-3" />
                        ⚡ {recipientAuthor.metadata.lud16 || recipientAuthor.metadata.lud06}
                      </p>
                    ) : (
                      <p className="text-xs text-destructive font-medium flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        No Lightning address configured
                      </p>
                    )}
                  </div>
                )}
                {zapCheckError && (
                  <div className="mt-2 p-2.5 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-xs flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>{zapCheckError}</span>
                  </div>
                )}
              </div>

              {/* Zap amount picker */}
              <div className="space-y-3">
                <Label>Zap amount</Label>
                <p className="text-xs text-muted-foreground">
                  Minimum is <strong>{minimumZap} sats</strong>
                  {lastZapAmount > 0 ? ` (previous pass was ${lastZapAmount} sats)` : ' (first pass)'}.
                </p>

                {/* Quick-add bump buttons */}
                <div className="flex gap-2 flex-wrap">
                  {ZAP_BUMPS.map((bump) => {
                    const target = minimumZap + bump;
                    return (
                      <button
                        key={bump}
                        type="button"
                        onClick={() => setCustomZapInput(String(target))}
                        className={cn(
                          'flex-1 min-w-[64px] py-2 px-3 rounded-lg border-2 text-sm font-semibold transition-all',
                          chosenZap === target
                            ? 'border-orange-500 bg-orange-50 dark:bg-orange-950/30 text-orange-700 dark:text-orange-300'
                            : 'border-border hover:border-orange-300 text-muted-foreground hover:text-foreground'
                        )}
                      >
                        +{bump.toLocaleString()}
                      </button>
                    );
                  })}
                </div>

                {/* Custom amount input */}
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={minimumZap}
                    placeholder={`${minimumZap} (minimum)`}
                    value={customZapInput}
                    onChange={(e) => setCustomZapInput(e.target.value)}
                    className="flex-1 font-mono"
                  />
                  <span className="text-sm text-muted-foreground shrink-0">sats</span>
                </div>

                {customZapInput && !isNaN(parsedCustom) && parsedCustom < minimumZap && (
                  <p className="text-xs text-destructive">
                    Must be at least {minimumZap} sats.
                  </p>
                )}

                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border border-border/50">
                  <span className="text-sm text-muted-foreground">You'll zap:</span>
                  <span className="text-lg font-bold text-orange-600">⚡ {chosenZap.toLocaleString()} sats</span>
                </div>
              </div>

              {/* Mark */}
              <div>
                <Label htmlFor="mark">Custom note (optional, ≤140 chars)</Label>
                <Textarea
                  id="mark"
                  placeholder="A message for posterity…"
                  value={mark}
                  onChange={(e) => setMark(e.target.value)}
                  maxLength={140}
                  className="mt-2"
                  rows={2}
                />
                <p className="text-xs text-muted-foreground mt-1">{mark.length}/140</p>
              </div>

              {zapError && (
                <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{zapError}</span>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="flex-1"
                  disabled={isZapping || isPublishing}
                >
                  Back
                </Button>
                <Button
                  onClick={handleZapAndPass}
                  disabled={isZapping || isPublishing || chosenZap < minimumZap || canReceiveZaps === false || isRecipientLoading}
                  className="flex-1 bg-orange-500 hover:bg-orange-600"
                >
                  {isZapping ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Zapping…</>
                  ) : isPublishing ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Publishing…</>
                  ) : canReceiveZaps === false ? (
                    <><AlertCircle className="w-4 h-4 mr-2" /> Cannot pass</>
                  ) : (
                    <><Zap className="w-4 h-4 mr-2" /> Zap {chosenZap.toLocaleString()} sats & Pass</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
